import React from 'react';
import { Paper, Typography, IconButton, Box, Button, Stack, TextField, Divider, InputAdornment, CircularProgress } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import { useParams, useLocation, useNavigate, createSearchParams, useSearchParams } from 'react-router-dom'
// import corpus from '../resources/corpus.json'
import { grey } from '@mui/material/colors'
import Banner from '../components/Banner'
import moment from 'moment'
import { TPageText, TBook, TSearchResult } from '../resources/types'
import { CorpusContext } from "../contexts/CorpusContext"

function SearchPage() {
  const corpusData = React.useContext(CorpusContext)
  const [urlInspected, setUrlInspected] = React.useState(false)
  const [processingDownloadRequest, setProcessingDownloadRequest] = React.useState(false);
  const navigate = useNavigate()
  const params = useParams()
  const { search } = useLocation()
  const [query] = useSearchParams()
  const [didSearch, setDidSearch] = React.useState(false)
  const [searchField, setSearchField] = React.useState('')
  const [searchResults, setSearchResults] = React.useState<TSearchResult[]>([])
  const [bookTitles, setBookTitles] = React.useState<{
    [code: string]: string;
  }>({})

  React.useEffect(
    () => {
      const titles: {
        [code: string]: string;
      } = {}
      corpusData.forEach(book => {
        titles[book.code] = book.title
      })
      setBookTitles(titles)
    },
    [corpusData]
  )





  // const query = React.useMemo(
  //   () => new URLSearchParams(search),
  //   [search]
  // )

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log(`handleSearchChange: search = ${event.target.value}`)
    setSearchField(event.target.value)
    setDidSearch(false)
  }

  const handleClearSearchField = () => {
    setSearchField('')
    setDidSearch(false)
  }

  const handleSearch = () => {
    if (!searchField) {
      return
    }
    const cleanedSearchField = searchField.replace(/\P{Script=Han}/gu, '')
    setSearchField(cleanedSearchField)
    console.log(`search with ${cleanedSearchField}`)
    if (!cleanedSearchField) {
      return
    }
    doSearch(cleanedSearchField)
  }

  const doSearch = (searchTerm: string) => {
    const needle = searchTerm.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    const results: TSearchResult[] = []
    const regex = new RegExp(`(.{0,16})${needle}(.{0,16})`)
    console.log(`regex = ${regex}`)
    corpusData.forEach(book => {
      book.contents.forEach((page, index) => {
        if (page.text.match(searchTerm)) {
          const match = regex.exec(page.text)
          console.log(JSON.stringify(match))
          results.push({
            book: book.code,
            pageIndex: index,
            page: page.page,
            text: page.text,
            pre: match?.[1] ?? '',
            needle: searchTerm,
            post: match?.[2] ?? ''
          })
        }
      })
    })
    console.log(JSON.stringify(results, null, 2))
    setSearchResults(results)
    setDidSearch(true)
  }

  React.useEffect(
    () => {
      if (!urlInspected) {
        const searchFromUrl = query.get('search')
        if (searchFromUrl) {
          const cleanedSearchField = searchFromUrl.replace(/\P{Script=Han}/gu, '')
          setSearchField(cleanedSearchField)
          console.log(`search with ${cleanedSearchField}`)
          doSearch(cleanedSearchField)
        }
        setUrlInspected(true)
      }
    },
    [query, urlInspected]
  )

  const handlePressEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const handleExportExcel = async () => {
    setProcessingDownloadRequest(true);
    try {
      const excelData = [
        ["文獻", "頁碼", "內文摘要"],
        ...searchResults
          .map((result: TSearchResult, index) => [
            bookTitles[result.book],
            result.page,
            `${result.pre}${result.needle}${result.post}`
          ])
      ];
      console.log(excelData);
      // const { default: xlsx } = await import("xlsx");
      const xlsx = await import("xlsx");
      console.log("book_new()");
      const wb = xlsx.utils.book_new();
      console.log("aoa_to_sheet()");
      const ws = xlsx.utils.aoa_to_sheet(excelData);
      xlsx.utils.book_append_sheet(wb, ws, searchField);
      xlsx.writeFile(
        wb,
        `search-${searchField}-${moment().format(
          "YYYYMMDDHHmmss"
        )}.xlsx`
      );
    } catch (error) {
      alert(`Error generating Excel file: ${(error as Error).toString()}`);
    } finally {
      setProcessingDownloadRequest(false);
    }
  };

  return (
    <Paper sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }} elevation={0}>
      <Banner/>
      <Stack sx={{ flex: 1, m: 0, pl: 1, pr: 1, mb: 1 }} display="flex" flexDirection="column">
        <Box display="flex" flexDirection="row" justifyContent="space-between">
          <Typography variant="h4"><SearchIcon fontSize="large" sx={{verticalAlign: 'middle', mb: 0.5, mr: 1}}/>搜尋文獻</Typography>
          <Box>
            <Button variant="outlined" startIcon={<MenuBookIcon/>} onClick={() => navigate({
              pathname: '/browse'
            })}>
              瀏覽文獻
            </Button>
          </Box>
        </Box>
        <Box mt={2} display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
          <TextField
            variant="standard"
            fullWidth
            label="文本搜尋"
            value={searchField}
            onChange={handleSearchChange}
            onKeyPress={handlePressEnter}
            InputProps={{
              endAdornment: [
                <IconButton key="delete" disabled={!searchField} onClick={handleClearSearchField}>
                  <ClearIcon />
                </IconButton>,
                <Divider key="divider" orientation="vertical" variant="middle" flexItem />,
                <InputAdornment key="search" position="end">
                  <IconButton color="primary" disabled={!searchField} onClick={handleSearch}>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ]
            }}
          />
        </Box>
        {
          didSearch && searchResults.length === 0 && <Box sx={{ pt: 1, mb: 4 }}>
            沒有搜尋結果
          </Box>
        }
        {
          searchResults.length > 0 && <Box sx={{ pt: 1, mb: 4 }}>
            <Box display="flex" flexDirection="row" alignItems="center" sx={{ pt: 1 }}>
              <Box flex={2}>
                {`搜尋結果：${searchResults.length}頁`}
              </Box>
              <Box>
              <Button
                disabled={processingDownloadRequest}
                size="small"
                variant="outlined"
                endIcon={
                  processingDownloadRequest ? (
                    <CircularProgress size={16} />
                  ) : (
                    <CloudDownloadIcon />
                  )
                }
                onClick={handleExportExcel}
              >
                下載Excel格式
              </Button>
              </Box>
            </Box>
            <Box display="flex" flexDirection="row" alignItems="center" sx={{ mt: 1, pt: 1, pb: 1, borderBottom: 'solid 2px lightgrey', backgroundColor: grey[300] }}>
              <Box sx={{ width: '5ch', mr: 2 }}>
              </Box>
              <Box flex={2} sx={{ fontSize: 'smaller', fontWeight: 'bold' }}>
                文獻
              </Box>
              <Box flex={0.5} sx={{ fontSize: 'smaller', fontWeight: 'bold' }}>
                頁碼
              </Box>
              <Box flex={8} sx={{ textAlign: 'center', fontSize: 'smaller', fontWeight: 'bold' }}>
                內文摘要
              </Box>
              <Typography sx={{ fontSize: 'smaller', fontWeight: 'bold', pr: 1 }}>查看</Typography>
            </Box>

            {searchResults.map((result: TSearchResult, index) => <Box key={index} display="flex" flexDirection="row" alignItems="center" sx={{ pt: 1, borderBottom: 'solid 1px lightgrey' }}>
              <Box sx={{ width: '5ch', mr: 2, textAlign: 'right' }}>
                {`${index + 1}.`}
              </Box>
              <Box flex={2}>
                {bookTitles[result.book]}
              </Box>
              <Box flex={0.5}>
                {result.page}
              </Box>
              <Box flex={4} sx={{ textAlign: 'right', letterSpacing: 1.1 }}>
                ...{result.pre}
              </Box>
              <Box flex={4} sx={{ textAlign: 'left', letterSpacing: 1.1 }}>
                <span style={{ backgroundColor: 'yellow' }}>{result.needle}</span>{result.post}...
              </Box>
              <IconButton
                onClick={() => navigate({
                  pathname: `/browse/${result.book}/${result.pageIndex + 1}`,
                  search: createSearchParams({ search: searchField }).toString()
                })}
              >
                <PlaylistPlayIcon />
              </IconButton>
            </Box>)}
          </Box>
        }
      </Stack>
    </Paper>
  );
}


export default SearchPage;
